import { z } from "zod";
import {
  addDependencyContract,
  cardDescriptionDesc,
  createPullRequestContract,
  postToChatContract,
  removeDependencyContract,
} from "@project/shared/tool-contracts";
import { defineTool } from "../harness/index.js";
import { defineContractTool } from "./contract-tool.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import type { AgentRunnerConfig } from "../runner-types.js";
import { textResult } from "./helpers.js";
import {
  hasUncommittedChanges,
  stageAndCommit,
  hasUnpushedCommits,
  pushToOrigin,
  getCurrentBranch,
} from "../runner/git-utils.js";

export function buildPostToChatTool(connection: AgentConnection) {
  return defineContractTool(
    postToChatContract,
    async ({ message, content, task_id, milestone }) => {
      // Cross-surface alias: the external conveyor-mcp surface calls this field
      // `content`, and models regularly carry that name over. Accept either.
      const text = message ?? content;
      if (text === undefined) {
        return textResult(
          JSON.stringify({
            posted: false,
            reason: "missing_message",
            hint: "Provide `message` (or its alias `content`) with the text to post.",
          }),
        );
      }
      try {
        if (task_id) {
          await connection.call("postChildChatMessage", {
            sessionId: connection.sessionId,
            childTaskId: task_id,
            message: text,
          });
          return textResult(JSON.stringify({ posted: true, target: `child:${task_id}` }));
        }
        const dedup = connection.checkAndTrackDuplicate(text);
        if (dedup.duplicate) {
          return textResult(
            JSON.stringify({
              posted: false,
              reason: "duplicate",
              matchedMessagePreview: dedup.matchedMessagePreview,
              hint: "A near-identical message (>70% word overlap) was posted within the last 30s. Rephrase with new information or skip this post.",
            }),
          );
        }
        await connection.call("postToChat", { message: text, milestone });
        return textResult(JSON.stringify({ posted: true }));
      } catch (error) {
        return textResult(
          `Failed to post message: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
  );
}

export function buildForceUpdateTaskStatusTool(connection: AgentConnection) {
  return defineTool(
    "force_update_task_status",
    "EMERGENCY ONLY: force-override a task's Kanban status. Use when an automatic transition failed and the task is wedged. Normal flow transitions status automatically.",
    {
      status: z
        .enum([
          "Planning",
          "Open",
          "InProgress",
          "ReviewPR",
          "ReviewDev",
          "ReviewLive",
          "Complete",
          "Cancelled",
        ])
        .describe("The new status for the task"),
      task_id: z
        .string()
        .optional()
        .describe("Child task ID to update. Omit to update the current task."),
    },
    async ({ status, task_id }) => {
      try {
        if (task_id) {
          await connection.call("updateChildStatus", {
            sessionId: connection.sessionId,
            childTaskId: task_id,
            status,
          });
          return textResult(`Child task ${task_id} status updated to ${status}.`);
        }
        await connection.call("updateTaskStatus", {
          sessionId: connection.sessionId,
          status,
          force: true,
        });
        return textResult(`Task status updated to ${status}.`);
      } catch (error) {
        return textResult(
          `Failed to update status: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
  );
}

// oxlint-disable-next-line max-lines-per-function -- PR creation involves git staging, committing, pushing, and API call
export function buildCreatePullRequestTool(connection: AgentConnection, config: AgentRunnerConfig) {
  return defineContractTool(
    createPullRequestContract,
    async ({ title, body, branch, baseBranch, commitMessage, skipVerify }) => {
      try {
        const cwd = config.workspaceDir;

        // Every git step below operates on the current checkout, so that is the
        // only branch the PR can legitimately track. Resolve it up front and send
        // it as an explicit head: when the server receives no head for a task
        // whose stored branch is missing or stale (a renamed card re-slugs
        // `githubBranch` behind our back), it mints a fresh branch from the task
        // title and opens the PR from that — a branch with zero commits, which
        // fails with "No commits between <base> and conveyor/<title-slug>".
        const headBranch = branch ?? (await getCurrentBranch(cwd));
        if (!headBranch) {
          return textResult(
            "Cannot create a pull request: the workspace is on a detached HEAD, so there is no branch to open the PR from. Check out a branch (e.g. `git checkout -b <branch>`) and retry, or pass `branch` explicitly.",
          );
        }

        if (await hasUncommittedChanges(cwd)) {
          const message =
            commitMessage || `${title}\n\nCo-Authored-By: Claude Sonnet 4 <noreply@anthropic.com>`;
          const commitHash = await stageAndCommit(cwd, message);
          if (commitHash) {
            connection.sendEvent({
              type: "message",
              content: `Auto-committed changes: ${commitHash.slice(0, 7)}`,
            });
          } else {
            return textResult(
              "Failed to stage and commit changes. Please check git status and commit manually before creating PR.",
            );
          }
        }

        if (await hasUnpushedCommits(cwd)) {
          const pushSuccess = await pushToOrigin(
            cwd,
            async () => {
              try {
                const result = await connection.call("refreshGithubToken", {
                  sessionId: connection.sessionId,
                });
                return result.token;
              } catch {
                return undefined;
              }
            },
            skipVerify ?? true,
          );
          if (pushSuccess) {
            connection.sendEvent({
              type: "message",
              content: "Auto-pushed committed changes to origin",
            });
          } else {
            return textResult(
              "Failed to push changes to origin. Please check git status and push manually before creating PR.",
            );
          }
        }

        const result = await connection.call("createPullRequest", {
          sessionId: connection.sessionId,
          title,
          body,
          head: headBranch,
          base: baseBranch,
        });
        connection.sendEvent({
          type: "pr_created",
          url: result.prUrl,
          number: result.prNumber,
        });
        const glossaryNote = result.glossaryNote ? `\n\n${result.glossaryNote}` : "";
        return textResult(
          `Pull request #${result.prNumber} created: ${result.prUrl}${glossaryNote}`,
        );
      } catch (error) {
        const msg = error instanceof Error ? error.message : "Unknown error";
        return textResult(
          `Failed to create pull request: ${msg}\n\nTroubleshooting:\n- Ensure all changes are committed and pushed to the remote branch\n- Check that the branch exists on the remote (run: git push -u origin HEAD)\n- Verify there isn't already an open PR for this branch\n- If git auth fails, the token may have expired — retry the operation`,
        );
      }
    },
  );
}

export function buildAddDependencyTool(connection: AgentConnection) {
  return defineContractTool(addDependencyContract, async ({ depends_on_slug_or_id }) => {
    try {
      await connection.call("addDependency", {
        sessionId: connection.sessionId,
        dependsOnSlugOrId: depends_on_slug_or_id,
      });
      return textResult(`Dependency added: this task now depends on "${depends_on_slug_or_id}"`);
    } catch (error) {
      return textResult(
        `Failed to add dependency: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  });
}

export function buildRemoveDependencyTool(connection: AgentConnection) {
  return defineContractTool(removeDependencyContract, async ({ depends_on_slug_or_id }) => {
    try {
      await connection.call("removeDependency", {
        sessionId: connection.sessionId,
        dependsOnSlugOrId: depends_on_slug_or_id,
      });
      return textResult("Dependency removed");
    } catch (error) {
      return textResult(
        `Failed to remove dependency: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  });
}

export function buildCreateFollowUpTaskTool(connection: AgentConnection) {
  return defineTool(
    "create_follow_up_task",
    "Create a follow-up task that depends on the current task. Use for out-of-scope work or cleanup that should land after this task merges. For blockers use add_dependency.",
    {
      title: z.string().describe("Follow-up task title"),
      description: z
        .string()
        .optional()
        .describe(cardDescriptionDesc("Brief description of the follow-up work")),
      plan: z.string().optional().describe("Implementation plan if known"),
      story_point_value: z
        .number()
        .optional()
        .describe("Story point estimate (1=Common, 2=Magic, 3=Rare, 5=Unique)"),
    },
    async ({ title, description, plan, story_point_value }) => {
      try {
        const result = await connection.call("createFollowUpTask", {
          sessionId: connection.sessionId,
          title,
          description,
          plan,
          storyPointValue: story_point_value,
        });
        return textResult(
          `Follow-up task created: "${title}" (slug: ${result.slug}). It depends on the current task and will be unblocked when this task is merged.`,
        );
      } catch (error) {
        return textResult(
          `Failed to create follow-up task: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
  );
}

export function buildCreateSuggestionTool(connection: AgentConnection) {
  return defineTool(
    "create_suggestion",
    "Suggest a feature, improvement, rule, or idea for the project. Duplicates are deduped and your upvote is recorded. For actionable work on this task open a follow-up task.",
    {
      title: z.string().describe("Short title for the suggestion"),
      description: z
        .string()
        .optional()
        .describe(
          "1-2 sentence description of what should change and why. Keep concise and project-focused.",
        ),
      tag_names: z.array(z.string()).optional().describe("Tag names to categorize the suggestion"),
    },
    async ({ title, description, tag_names }) => {
      try {
        const result = await connection.call("createSuggestion", {
          sessionId: connection.sessionId,
          title,
          description,
          tagNames: tag_names,
        });
        if (result.merged) {
          return textResult(
            `Your suggestion was merged into an existing one (ID: ${result.mergedIntoId ?? result.id}). Your upvote has been recorded.`,
          );
        }
        return textResult(`Suggestion created (ID: ${result.id}).`);
      } catch (error) {
        return textResult(
          `Failed to create suggestion: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
  );
}

export function buildVoteSuggestionTool(connection: AgentConnection) {
  return defineTool(
    "vote_suggestion",
    "Vote +1 or -1 on a project suggestion. Use to express support or disagreement with a specific suggestion returned by get_suggestions.",
    {
      suggestion_id: z.string().describe("The suggestion ID to vote on"),
      value: z
        .number()
        .refine((v) => v === 1 || v === -1, { message: "Value must be 1 or -1" })
        .describe("+1 to upvote, -1 to downvote"),
    },
    async ({ suggestion_id, value }) => {
      try {
        const result = await connection.call("voteSuggestion", {
          sessionId: connection.sessionId,
          suggestionId: suggestion_id,
          value: value as 1 | -1,
        });
        return textResult(`Vote recorded. Current score: ${result.score}`);
      } catch (error) {
        return textResult(
          `Failed to vote: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
  );
}

/** All write/mutation tools (excluding PR creation which needs config). */
export function buildMutationTools(connection: AgentConnection, config: AgentRunnerConfig) {
  return [
    buildPostToChatTool(connection),
    buildCreatePullRequestTool(connection, config),
    buildAddDependencyTool(connection),
    buildRemoveDependencyTool(connection),
    buildCreateFollowUpTaskTool(connection),
    buildCreateSuggestionTool(connection),
    buildVoteSuggestionTool(connection),
  ];
}
